// Self-contained visual confirmation of the dual-schema monitoring decode — BOTH directions.
//
// OLD edge (development): a raw, non-enveloped FrontEndMessage is decoded with the legacy
// schema and translated BY NAME into the NEW (config_wizard / flattened-valued-messages)
// types, then rendered through the SAME display helpers the monitoring UI uses.
//
// NEW edge (config_wizard): a NEW frame is built with the generated *T Object API, packed,
// LN2E-enveloped exactly as the new cloud emits it, and run through the SAME dispatch the
// live UI uses (isEnvelope -> unwrapEnvelope -> decodeBatchedFrontEndMessage). This proves the
// dual-compat dispatch did not regress the NEW path.
//
// No backend, websocket, or auth — pure render of the real production code.

import React from "react";
import * as flatbuffers from "flatbuffers";

import { Fault } from "../../flatbuffers/generated/fault/fault";
import { Service } from "../../flatbuffers/generated/service/service";
import { Usage } from "../../flatbuffers/generated/usage/usage";
import {
  directionDisplayName,
  doorZoneDisplayName,
} from "../../flatbuffers/types/enumDisplayUtils";
import {
  translateFault,
  translateService,
  translateUsageValue,
} from "../../flatbuffers/decoders/legacyMonitoringBridge";
import { isEnvelope, unwrapEnvelope, wrapEnvelope } from "../../flatbuffers/envelope";
import { decodeBatchedFrontEndMessage } from "../../flatbuffers/decoders/frontEndMessageDecoder";

// OLD (legacy / development) generated types.
import { DeviceServiceContent as OldDeviceServiceContent } from "../../flatbuffers/generated_legacy/service/device-service-content";
import { ElevatorFault as OldElevatorFault } from "../../flatbuffers/generated_legacy/fault/elevator-fault";
import { ElevatorFaultMessageT as OldElevatorFaultMessageT } from "../../flatbuffers/generated_legacy/fault/elevator-fault-message";
import { ElevatorService as OldElevatorService } from "../../flatbuffers/generated_legacy/service/elevator-service";
import { ElevatorServiceMessageT as OldElevatorServiceMessageT } from "../../flatbuffers/generated_legacy/service/elevator-service-message";
import { ElevatorUsage as OldElevatorUsage } from "../../flatbuffers/generated_legacy/usage/elevator-usage";
import { FaultContent as OldFaultContent } from "../../flatbuffers/generated_legacy/fault/fault-content";
import { FaultMessageT as OldFaultMessageT } from "../../flatbuffers/generated_legacy/fault/fault-message";
import { ServiceMessageT as OldServiceMessageT } from "../../flatbuffers/generated_legacy/service/service-message";

// NEW (config_wizard / flattened-valued-messages) generated types — for the enveloped round-trip.
import {
  BatchedFrontEndMessage,
  BatchedFrontEndMessageT,
} from "../../flatbuffers/generated/batched-front-end/batched-front-end-message";
import { FrontEndMessageT } from "../../flatbuffers/generated/front-end/front-end-message";
import { UuidT } from "../../flatbuffers/generated/front-end/uuid";
import { U128T } from "../../flatbuffers/generated/u128/u128";
import { ValuedMessageT } from "../../flatbuffers/generated/ln2/valued-message";
import { MessageT } from "../../flatbuffers/generated/ln2/message";
import { EncodedMessageContent } from "../../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../../flatbuffers/generated/ln2/message-content";
import { BitMaskMessageT } from "../../flatbuffers/generated/ln2/bit-mask-message";
import { StatusMessageT } from "../../flatbuffers/generated/status/status-message";
import { DeviceStatusContent } from "../../flatbuffers/generated/status/device-status-content";
import { ElevatorStatusMessageT } from "../../flatbuffers/generated/status/elevator-status-message";
import { StatusVariant } from "../../flatbuffers/generated/status/status-variant";
import { NormalStatusT } from "../../flatbuffers/generated/status/normal-status";
import { Status } from "../../flatbuffers/generated/status/status";

interface Row {
  field: string;
  oldName: string;
  oldValue: number;
  newValue: number | null;
  newName: string;
  rendered: string;
  ok: boolean;
}

function newServiceName(msg: ReturnType<typeof translateService>): {
  v: number | null;
  name: string;
} {
  const v = (msg?.message as { message?: { message?: number } } | null)?.message?.message ?? null;
  return { v, name: v == null ? "—" : (Service[v] ?? `Service(${v})`) };
}

function newFaultName(msg: ReturnType<typeof translateFault>): { v: number | null; name: string } {
  const v = (msg?.fault as { fault?: { message?: number } } | null)?.fault?.message ?? null;
  return { v, name: v == null ? "—" : (Fault[v] ?? `Fault(${v})`) };
}

function buildRows(): Row[] {
  // Service mode: OLD ElevatorService.VIP
  const svc = translateService(
    new OldServiceMessageT(
      OldDeviceServiceContent.ElevatorServiceMessage,
      new OldElevatorServiceMessageT(OldElevatorService.VIP),
    ),
  );
  const svcNew = newServiceName(svc);

  // Active fault: OLD ElevatorFault.OutOfService — renumbered between schemas
  // (OLD 244 -> NEW 500), so only a by-name map renders it correctly.
  const flt = translateFault(
    new OldFaultMessageT(
      OldFaultContent.ElevatorFaultMessage,
      new OldElevatorFaultMessageT(OldElevatorFault.OutOfService),
    ),
  );
  const fltNew = newFaultName(flt);

  // Direction: OLD ElevatorUsage.Up
  const dirNew = translateUsageValue(OldElevatorUsage.Up);
  // Door zone: OLD ElevatorUsage.InDoorZone
  const dzNew = translateUsageValue(OldElevatorUsage.InDoorZone);

  return [
    {
      field: "Service mode",
      oldName: "VIP",
      oldValue: OldElevatorService.VIP,
      newValue: svcNew.v,
      newName: svcNew.name,
      rendered: svcNew.name,
      ok: svcNew.name === "VIP",
    },
    {
      field: "Active fault",
      oldName: "OutOfService",
      oldValue: OldElevatorFault.OutOfService,
      newValue: fltNew.v,
      newName: fltNew.name,
      rendered: fltNew.name,
      ok: fltNew.name === "OutOfService",
    },
    {
      field: "Direction",
      oldName: "Up",
      oldValue: OldElevatorUsage.Up,
      newValue: dirNew,
      newName: dirNew == null ? "—" : Usage[dirNew],
      rendered: directionDisplayName(dirNew),
      ok: dirNew === Usage.Up,
    },
    {
      field: "Door zone",
      oldName: "InDoorZone",
      oldValue: OldElevatorUsage.InDoorZone,
      newValue: dzNew,
      newName: dzNew == null ? "—" : Usage[dzNew],
      rendered: doorZoneDisplayName(dzNew),
      ok: dzNew === Usage.InDoorZone,
    },
  ];
}

// A 64-char hex schema hash. isEnvelope() checks the LN2E magic + length, not the hash value,
// and unwrapEnvelope() strips the fixed 42-byte header regardless — so a placeholder is fine
// for exercising the dispatch + decode round-trip.
const NEW_SCHEMA_HASH = "00".repeat(32);

interface NewEdgeProof {
  enveloped: boolean;
  routedToNewPath: boolean;
  decoded: boolean;
  count: number;
  ok: boolean;
}

// Build a NEW (config_wizard) frame exactly as the new cloud does, LN2E-envelope it, then run
// the SAME dispatch the live monitoring UI uses (MonitoringWebSocket.onMessageFlatBuffer):
//   isEnvelope(raw) === true  ->  decodeBatchedFrontEndMessage(unwrapEnvelope(raw))
function buildNewEdgeProof(): NewEdgeProof {
  const normalStatus = new NormalStatusT(Status.DoorOpenCommand);
  const elevatorStatus = new ElevatorStatusMessageT(StatusVariant.NormalStatus, normalStatus);
  const statusMessage = new StatusMessageT(
    DeviceStatusContent.ElevatorStatusMessage,
    elevatorStatus,
  );
  const bitmask = new BitMaskMessageT(MessageContent.Status_StatusMessage, statusMessage);
  const valued = new ValuedMessageT(
    new MessageT(EncodedMessageContent.BitMaskMessage, bitmask),
    true,
  );
  const fem = new FrontEndMessageT(
    new UuidT(new U128T(BigInt(0x1111), BigInt(0x2222))),
    new UuidT(new U128T(BigInt(0x3333), BigInt(0x4444))),
    BigInt(1000),
    valued,
  );
  const batch = new BatchedFrontEndMessageT(BigInt(99999), [fem]);
  const builder = new flatbuffers.Builder(1024);
  BatchedFrontEndMessage.finishSizePrefixedBatchedFrontEndMessageBuffer(
    builder,
    batch.pack(builder),
  );
  const payload = builder.asUint8Array();

  const raw = wrapEnvelope(payload, NEW_SCHEMA_HASH);
  const routedToNewPath = isEnvelope(raw); // a NEW edge envelopes -> NOT the legacy branch
  const decodedBatch = routedToNewPath ? decodeBatchedFrontEndMessage(unwrapEnvelope(raw)) : null;
  const count = decodedBatch?.messages.length ?? 0;
  return {
    enveloped: raw.length > payload.length,
    routedToNewPath,
    decoded: decodedBatch !== null,
    count,
    ok: routedToNewPath && decodedBatch !== null && count === 1,
  };
}

// The NEW-edge field renders: native config_wizard enum values through the SAME production
// display helpers. (The full per-field DECODE round-trip is asserted by the demo's L0 layer.)
const NEW_EDGE_ROWS: { field: string; newName: string; newValue: number; rendered: string }[] = [
  { field: "Service mode", newName: "VIP", newValue: Service.VIP, rendered: Service[Service.VIP] },
  {
    field: "Active fault",
    newName: "OutOfService",
    newValue: Fault.OutOfService,
    rendered: Fault[Fault.OutOfService],
  },
  {
    field: "Direction",
    newName: "Up",
    newValue: Usage.Up,
    rendered: directionDisplayName(Usage.Up),
  },
  {
    field: "Door zone",
    newName: "InDoorZone",
    newValue: Usage.InDoorZone,
    rendered: doorZoneDisplayName(Usage.InDoorZone),
  },
];

const cell: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid #2a2f3a" };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, Menlo, monospace" };
const tableStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  maxWidth: 980,
  background: "#151a23",
  borderRadius: 8,
  overflow: "hidden",
};
const theadRow: React.CSSProperties = {
  background: "#1c222e",
  textAlign: "left",
  color: "#9aa4b2",
};

function badge(ok: boolean, text: string): React.ReactElement {
  return (
    <div
      style={{
        display: "inline-block",
        marginBottom: 14,
        padding: "6px 14px",
        borderRadius: 6,
        fontWeight: 600,
        background: ok ? "#10391f" : "#3a1010",
        color: ok ? "#5ee08a" : "#ff6b6b",
      }}
    >
      {text}
    </div>
  );
}

export default function DualCompatDemoPage(): React.ReactElement {
  const rows = buildRows();
  const oldOk = rows.every((r) => r.ok);
  const proof = buildNewEdgeProof();
  const allOk = oldOk && proof.ok;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0f131a",
        color: "#e6e9ef",
        padding: 32,
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Dual-schema decode — visual confirmation</h1>
      <p style={{ color: "#9aa4b2", marginTop: 0, maxWidth: 820 }}>
        During a fleet rollout the shared UI must render monitoring from BOTH a NEW (config_wizard)
        edge and an OLD (development) edge. The single discriminator is <code>isEnvelope</code>: a
        NEW edge LN2E-envelopes every frame; an OLD edge sends a raw FrontEndMessage. Both paths are
        exercised below with the real production code.
      </p>
      <div
        data-testid="dual-compat-status"
        style={{
          display: "inline-block",
          marginBottom: 22,
          padding: "8px 16px",
          borderRadius: 6,
          fontWeight: 700,
          fontSize: 15,
          background: allOk ? "#10391f" : "#3a1010",
          color: allOk ? "#5ee08a" : "#ff6b6b",
        }}
      >
        {allOk ? "✓ BOTH EDGES DECODE & RENDER CORRECTLY" : "✗ MISMATCH"}
      </div>

      {/* ── OLD edge ────────────────────────────────────────────────────────── */}
      <h2 style={{ fontSize: 16, margin: "8px 0 6px" }}>
        OLD edge (development) — legacy decode + translate BY NAME
      </h2>
      <p style={{ color: "#9aa4b2", marginTop: 0, maxWidth: 820, fontSize: 14 }}>
        The flatten renumbered every value, so a correct translation must map by name, not pass the
        OLD integer through.
      </p>
      <div data-testid="old-edge-status">
        {badge(oldOk, oldOk ? "✓ OLD fields translated & rendered" : "✗ OLD mismatch")}
      </div>
      <table style={tableStyle}>
        <thead>
          <tr style={theadRow}>
            <th style={cell}>Field</th>
            <th style={cell}>OLD enum (value)</th>
            <th style={cell}>→</th>
            <th style={cell}>NEW enum (value)</th>
            <th style={cell}>Rendered in UI</th>
            <th style={cell}>OK</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field}>
              <td style={{ ...cell, fontWeight: 600 }}>{r.field}</td>
              <td style={{ ...cell, ...mono, color: "#f0b25b" }}>
                {r.oldName} ({r.oldValue})
              </td>
              <td style={cell}>→</td>
              <td style={{ ...cell, ...mono, color: "#7aa2ff" }}>
                {r.newName} ({r.newValue ?? "—"})
              </td>
              <td style={{ ...cell, fontWeight: 600 }}>{r.rendered}</td>
              <td style={{ ...cell, color: r.ok ? "#5ee08a" : "#ff6b6b", fontWeight: 700 }}>
                {r.ok ? "✓" : "✗"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── NEW edge ────────────────────────────────────────────────────────── */}
      <h2 style={{ fontSize: 16, margin: "30px 0 6px" }}>
        NEW edge (config_wizard) — enveloped frame, native decode (unregressed)
      </h2>
      <p style={{ color: "#9aa4b2", marginTop: 0, maxWidth: 820, fontSize: 14 }}>
        A NEW frame is built with the generated <code>*T</code> Object API, LN2E-enveloped, and run
        through the SAME dispatch the live UI uses.
      </p>
      <div data-testid="new-edge-status">
        {badge(
          proof.ok,
          proof.ok
            ? `✓ NEW frame enveloped · isEnvelope=true (routed to NEW path) · decoded ${proof.count}/1 message`
            : "✗ NEW round-trip failed",
        )}
      </div>
      <table style={tableStyle}>
        <thead>
          <tr style={theadRow}>
            <th style={cell}>Field</th>
            <th style={cell}>NEW enum (value)</th>
            <th style={cell}>Rendered in UI</th>
          </tr>
        </thead>
        <tbody>
          {NEW_EDGE_ROWS.map((r) => (
            <tr key={r.field}>
              <td style={{ ...cell, fontWeight: 600 }}>{r.field}</td>
              <td style={{ ...cell, ...mono, color: "#7aa2ff" }}>
                {r.newName} ({r.newValue})
              </td>
              <td style={{ ...cell, fontWeight: 600 }}>{r.rendered}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ color: "#6b7280", marginTop: 18, fontSize: 13, maxWidth: 820 }}>
        Both edges render identical labels (e.g. <b>OutOfService</b>, <b>In Door Zone</b>) — the OLD
        frame mapped by name, the NEW frame decoded natively. Per-field NEW decode round-trips are
        asserted comprehensively by the demo&apos;s L0 layer (71 tests).
      </p>
    </div>
  );
}
